/**
 * Dropdown content type schema — ACH slug `qt-dropdown`
 */

import type {
    CompositeQuestionsLayoutFields,
    ContentConfig,
    LayoutConfig,
    ScoringConfig,
} from '@ai-content-hub/content-core';
import type { FITBElementData } from '@ai-content-hub/content-elements';
import { defaultPageData } from '@ai-content-hub/content-elements';
import type { PageElementData } from '@ai-content-hub/content-elements';
import { generateId } from '@ai-content-hub/content-core';
import { fitbElementSchema } from '@ai-content-hub/content-elements';

export type { PageElementData } from '@ai-content-hub/content-elements';

export type QtDropdownSplitRatio = '50-50' | '60-40' | '40-60' | '70-30' | '30-70';

export interface QtDropdownLayoutConfig extends LayoutConfig {
    splitRatio?: QtDropdownSplitRatio;
    displayMode?: 'all' | 'step' | 'tabs';
    inlineSinglePageNav?: boolean;
}

export interface QtDropdownScoringConfig extends ScoringConfig {
    pointsPerQuestion?: number;
    passingThreshold?: number;
}

export interface QtDropdownItem {
    id: string;
    data: FITBElementData;
    points?: number;
}

export interface QtDropdownData extends CompositeQuestionsLayoutFields {
    title?: string;
    instructions?: string;
    pageContent: PageElementData;
    items: QtDropdownItem[];
}

export interface QtDropdownConfig extends Omit<ContentConfig, 'layout' | 'scoring'> {
    layout?: QtDropdownLayoutConfig;
    scoring?: QtDropdownScoringConfig;
    /** Passed to FITB (dropdown bank / option order) */
    shuffleOptions?: boolean;
    caseSensitive?: boolean;
}

export interface QtDropdownContent {
    id: string;
    type: 'qt-dropdown';
    version: string;
    data: QtDropdownData;
    config?: QtDropdownConfig;
}

export const defaultQtDropdownI18n = {
    en: {
        submit: 'Submit',
        retry: 'Try Again',
        noQuestions: 'No questions available',
        contentTab: 'Content',
        questionsTab: 'Questions',
        configTab: 'Configuration',
        titleLabel: 'Title',
        titlePlaceholder: 'Exercise title',
        instructionsLabel: 'Instructions (optional)',
        instructionsPlaceholder: 'Learner-facing instructions',
        exercisesListTitle: 'Exercises',
        emptyExercisesMsg: 'No exercises yet. Add a dropdown passage.',
        addSection: 'Add section',
        removeSection: 'Remove section',
        dropdownBlanksPreview: 'Dropdown blanks',
        addExercise: 'Add exercise',
        sectionDetailTitle: 'Section',
        exerciseDetailTitle: 'Exercise',
        dropdownBadge: 'Dropdown',
        detailEmptyMessage: 'Select an exercise, a section, or add one to start editing.',
        removeExercise: 'Remove exercise',
        exerciseHeading: 'Exercise {{index}}',
    },
    vi: {
        submit: 'Gửi',
        retry: 'Thử lại',
        noQuestions: 'Chưa có câu hỏi',
        contentTab: 'Nội dung',
        questionsTab: 'Câu hỏi',
        configTab: 'Cấu hình',
        titleLabel: 'Tiêu đề',
        titlePlaceholder: 'Tiêu đề bài tập',
        instructionsLabel: 'Hướng dẫn (tuỳ chọn)',
        instructionsPlaceholder: 'Hướng dẫn cho học viên',
        exercisesListTitle: 'Danh sách bài tập',
        emptyExercisesMsg: 'Chưa có bài tập nào. Hãy thêm đoạn văn điền từ.',
        addSection: 'Thêm mục',
        removeSection: 'Xóa mục',
        dropdownBlanksPreview: 'Điền từ vào chỗ trống',
        addExercise: 'Thêm bài tập',
        sectionDetailTitle: 'Mục',
        exerciseDetailTitle: 'Bài tập',
        dropdownBadge: 'Chọn từ',
        detailEmptyMessage: 'Chọn một bài tập, một mục, hoặc thêm mới để bắt đầu.',
        removeExercise: 'Xóa bài tập',
        exerciseHeading: 'Bài tập {{index}}',
    },
};

export const defaultQtDropdownConfig: QtDropdownConfig = {
    layout: {
        type: 'horizontal',
        splitRatio: '50-50',
        displayMode: 'all',
    },
    shuffleOptions: true,
    caseSensitive: false,
    behavior: {
        allowRetry: true,
        showCorrectAnswer: true,
        showExplanation: true,
        examMode: false,
        instantFeedback: false,
    },
    scoring: {
        pointsPerQuestion: 1,
        partialCreditEnabled: true,
        passingThreshold: 0.7,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultQtDropdownI18n,
    },
};

export const defaultQtDropdownData: QtDropdownData = {
    // EAA-2082: a new Dropdown starts with an EMPTY title so the editor shows only the
    // placeholder. It used to default to the type's name ("Dropdown blanks"), which the
    // question-bank page also picked up as the content title, so a learner-facing title
    // nobody typed got saved.
    title: '',
    instructions: '',
    pageContent: { ...defaultPageData },
    items: [],
};

export function createQtDropdownItem(id?: string): QtDropdownItem {
    return {
        id: id ?? generateId(),
        data: { ...(fitbElementSchema.defaultData as FITBElementData) },
    };
}

/**
 * Wrap legacy answers keyed by blank id when there is a single item.
 */
export function normalizeQtDropdownAnswerMap(
    data: QtDropdownData,
    answer: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
    if (!answer || typeof answer !== 'object' || Array.isArray(answer)) {
        return {};
    }
    const items = data.items ?? [];
    if (items.length === 1) {
        const item = items[0];
        const nested = answer[item.id];
        if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
            return { ...answer };
        }
        const blanks = item.data.blanks ?? [];
        for (const b of blanks) {
            if (Object.prototype.hasOwnProperty.call(answer, b.id)) {
                return { [item.id]: { ...answer } };
            }
        }
    }
    return { ...answer };
}

export const qtDropdownSchema = {
    type: 'qt-dropdown' as const,
    title: 'Dropdown',
    description: 'Reading content with multiple dropdown fill-in-the-blank exercises',
    version: '1.0.0',
    category: 'assessment' as const,
    icon: 'ListOrdered',
    tags: ['qt-dropdown', 'fitb', 'dropdown', 'ach'],
    defaultData: defaultQtDropdownData,
    defaultConfig: defaultQtDropdownConfig,
};
